import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { hrmsGetRequest, hrmsPostRequest, hrmsUpdateRequest, hrmsDeleteRequest } from "../services/index";

const API_URL = "/api/annual-personal-development-plans";

// Create APDP plan (Employee) - POST /
export const createAPDP = createAsyncThunk(
  "apdp/create",
  async (planData, { rejectWithValue }) => {
    try {
      const response = await hrmsPostRequest(API_URL, planData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Create multiple APDP plans (Employee) - POST /multiple
export const createMultipleAPDPs = createAsyncThunk(
  "apdp/createMultiple",
  async (plansData, { rejectWithValue }) => {
    try {
      const response = await hrmsPostRequest(`${API_URL}/multiple`, { plans: plansData });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Accept APDP plan (Admin) - PUT /accept/:id
export const acceptAPDP = createAsyncThunk(
  "apdp/accept",
  async ({ planId, adminNotes }, { rejectWithValue }) => {
    try {
      const body = adminNotes ? { adminNotes } : {};
      const response = await hrmsUpdateRequest(`${API_URL}/accept/${planId}`, body);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Reject APDP plan (Admin) - PUT /reject/:id
export const rejectAPDP = createAsyncThunk(
  "apdp/reject",
  async ({ planId, adminNotes }, { rejectWithValue }) => {
    try {
      const response = await hrmsUpdateRequest(`${API_URL}/reject/${planId}`, { adminNotes });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Update APDP plan (Employee) - PUT /update/:id
export const updateAPDP = createAsyncThunk(
  "apdp/update",
  async ({ planId, updateData }, { rejectWithValue }) => {
    try {
      const response = await hrmsUpdateRequest(`${API_URL}/update/${planId}`, updateData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Get all APDP plans (Admin) - GET /
export const fetchAllAPDPs = createAsyncThunk(
  "apdp/fetchAll",
  async (_, { rejectWithValue }) => {
    try {
      const response = await hrmsGetRequest(API_URL);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Get APDP plans by status (Admin) - GET /status/:status
export const fetchAPDPsByStatus = createAsyncThunk(
  "apdp/fetchByStatus",
  async (status, { rejectWithValue }) => {
    try {
      const response = await hrmsGetRequest(`${API_URL}/status/${status}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Get APDP plans by year (Admin) - GET /year/:year
export const fetchAPDPsByYear = createAsyncThunk(
  "apdp/fetchByYear",
  async (year, { rejectWithValue }) => {
    try {
      const response = await hrmsGetRequest(`${API_URL}/year/${year}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Get APDP plans by teacher email (Employee/Admin) - GET /teacher/:teacherEmail
export const fetchAPDPsByTeacher = createAsyncThunk(
  "apdp/fetchByTeacher",
  async (teacherEmail, { rejectWithValue }) => {
    try {
      const response = await hrmsGetRequest(`${API_URL}/teacher/${teacherEmail}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Get APDP plan by ID - GET /:id
export const fetchAPDPById = createAsyncThunk(
  "apdp/fetchById",
  async (planId, { rejectWithValue }) => {
    try {
      const response = await hrmsGetRequest(`${API_URL}/${planId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Delete APDP plan (Employee) - DELETE /:id
export const deleteAPDP = createAsyncThunk(
  "apdp/delete",
  async (planId, { rejectWithValue }) => {
    try {
      const response = await hrmsDeleteRequest(`${API_URL}/${planId}`);
      return { planId, message: response.data.message };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

const initialState = {
  plans: [],
  plansByYear: {},
  count: {
    pending: 0,
    accepted: 0,
    rejected: 0,
  },
  loading: false,
  error: null,
};

const annualPersonalDevelopmentPlanSlice = createSlice({
  name: "annualPersonalDevelopmentPlan",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    resetAPDPs: (state) => {
      state.plans = [];
      state.plansByYear = {};
      state.count = {
        pending: 0,
        accepted: 0,
        rejected: 0,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      // Create APDP
      .addCase(createAPDP.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createAPDP.fulfilled, (state, action) => {
        state.loading = false;
        state.plans.push(action.payload);
        if (action.payload.status === "pending") {
          state.count.pending += 1;
        }
      })
      .addCase(createAPDP.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create Multiple APDPs
      .addCase(createMultipleAPDPs.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createMultipleAPDPs.fulfilled, (state, action) => {
        state.loading = false;
        state.plans.push(...action.payload);
        state.count.pending += action.payload.length;
      })
      .addCase(createMultipleAPDPs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Accept APDP
      .addCase(acceptAPDP.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(acceptAPDP.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.plans.findIndex((p) => p.id === action.payload.id);
        if (index !== -1) {
          const oldStatus = state.plans[index].status;
          state.plans[index] = action.payload;
          if (oldStatus === "pending") {
            state.count.pending -= 1;
          } else if (oldStatus === "rejected") {
            state.count.rejected -= 1;
          }
          state.count.accepted += 1;
        }
      })
      .addCase(acceptAPDP.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Reject APDP
      .addCase(rejectAPDP.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(rejectAPDP.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.plans.findIndex((p) => p.id === action.payload.id);
        if (index !== -1) {
          const oldStatus = state.plans[index].status;
          state.plans[index] = action.payload;
          if (oldStatus === "pending") {
            state.count.pending -= 1;
          } else if (oldStatus === "accepted") {
            state.count.accepted -= 1;
          }
          state.count.rejected += 1;
        }
      })
      .addCase(rejectAPDP.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update APDP
      .addCase(updateAPDP.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateAPDP.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.plans.findIndex((p) => p.id === action.payload.id);
        if (index !== -1) {
          const oldStatus = state.plans[index].status;
          state.plans[index] = action.payload;
          if (oldStatus === "rejected") {
            state.count.rejected -= 1;
            state.count.pending += 1;
          }
        }
      })
      .addCase(updateAPDP.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch all APDPs
      .addCase(fetchAllAPDPs.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllAPDPs.fulfilled, (state, action) => {
        state.loading = false;
        state.plans = action.payload;
        state.count = {
          pending: action.payload.filter((p) => p.status === "pending").length,
          accepted: action.payload.filter((p) => p.status === "accepted").length,
          rejected: action.payload.filter((p) => p.status === "rejected").length,
        };
      })
      .addCase(fetchAllAPDPs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch by status
      .addCase(fetchAPDPsByStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAPDPsByStatus.fulfilled, (state, action) => {
        state.loading = false;
        state.plans = action.payload;
      })
      .addCase(fetchAPDPsByStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch by year
      .addCase(fetchAPDPsByYear.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAPDPsByYear.fulfilled, (state, action) => {
        state.loading = false;
        state.plans = action.payload;
      })
      .addCase(fetchAPDPsByYear.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch by teacher
      .addCase(fetchAPDPsByTeacher.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAPDPsByTeacher.fulfilled, (state, action) => {
        state.loading = false;
        state.plans = action.payload.plans || [];
        state.plansByYear = action.payload.plansByYear || {};
        state.count = action.payload.count || {
          pending: 0,
          accepted: 0,
          rejected: 0,
        };
      })
      .addCase(fetchAPDPsByTeacher.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch by ID
      .addCase(fetchAPDPById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAPDPById.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.plans.findIndex((p) => p.id === action.payload.id);
        if (index !== -1) {
          state.plans[index] = action.payload;
        } else {
          state.plans.push(action.payload);
        }
      })
      .addCase(fetchAPDPById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Delete APDP
      .addCase(deleteAPDP.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteAPDP.fulfilled, (state, action) => {
        state.loading = false;
        const deletedPlan = state.plans.find((p) => p.id === action.payload.planId);
        if (deletedPlan) {
          if (deletedPlan.status === "pending") {
            state.count.pending -= 1;
          } else if (deletedPlan.status === "rejected") {
            state.count.rejected -= 1;
          }
        }
        state.plans = state.plans.filter((p) => p.id !== action.payload.planId);
      })
      .addCase(deleteAPDP.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearError, resetAPDPs } = annualPersonalDevelopmentPlanSlice.actions;
export default annualPersonalDevelopmentPlanSlice.reducer;

